/** The filter row above a table: chips that open option lists, a search box, saved views, a result
 * count and an actions slot (export).
 *
 * **Controlled, and it emits one object.** `state` is `{ search, values }` where `values` maps a
 * filter id to the selected option values; every change calls `onChange` with a complete new
 * `FilterState`, so a screen keeps one piece of state (and can put it in the URL) instead of one per
 * chip. Search is debounced here (`debounceMs`) so a screen does not refetch per keystroke.
 *
 * **Counts are the caller's.** `resultCount` is `{ shown, total }` as the server reported them;
 * this component prints "Showing 25 of 1,201 figures" and never counts rows itself.
 *
 * Keyboard: a chip opens on Enter, Space or ArrowDown; the list is a roving-focus `listbox`
 * (ArrowUp/Down, Home/End, Enter or Space toggles, Escape closes and returns to the chip, Tab leaves).
 * A single-select closes on choosing. An option list is a listbox rather than a menu because it
 * holds selectable values, not commands.
 */

import { BookmarkSimple, Check, FunnelSimple, MagnifyingGlass, X } from "@phosphor-icons/react";
import { CaretDown } from "@phosphor-icons/react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";

import { quantity } from "../../lib/format";
import styles from "./FilterBar.module.css";
import { EMPTY_FILTERS, activeFilterCount } from "./filters";
import type { FilterDef, FilterOption, FilterState } from "./filters";
import { FilterChips, SEARCH_OPTIONS_OVER } from "./tablePower/FilterChips";
import { usePopup } from "./usePopup";

/** A count with thousands separators, through the product's one formatter. */
const number = { format: (value: number): string => quantity(String(value)) };

/** A single-choice filter as a native, styled `select` (the table page's filter line): the platform's
 * own control and menu, with the platform's arrow removed and ours laid over it. "All" is the empty
 * value, so a filter that is not narrowing the table is the one that says so. */
function NativeSelect({
  def,
  selected,
  onChange,
}: {
  def: FilterDef;
  selected: readonly string[];
  onChange: (next: readonly string[]) => void;
}) {
  const active = selected.length > 0;
  return (
    <span className={`${styles.selectWrap} ${active ? styles.selectOn : ""}`}>
      <select
        className={styles.select}
        aria-label={def.label}
        value={selected[0] ?? ""}
        data-testid={`filter-${def.id}`}
        data-active={active}
        onChange={(event) => onChange(event.target.value === "" ? [] : [event.target.value])}
      >
        <option value="">{`${def.label}: ${def.allLabel ?? "All"}`}</option>
        {def.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <CaretDown className={styles.selectCaret} size={12} weight="bold" aria-hidden="true" />
    </span>
  );
}

function Chip({
  def,
  selected,
  onChange,
}: {
  def: FilterDef;
  selected: readonly string[];
  onChange: (next: readonly string[]) => void;
}) {
  const multi = (def.mode ?? "multi") === "multi";
  const { open, setOpen, close, triggerRef, popupRef } = usePopup();
  const listId = useId();
  const [index, setIndex] = useState(0);
  const [find, setFind] = useState("");
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const findRef = useRef<HTMLInputElement>(null);
  // Whether the focus has moved from the search box into the options.
  const inOptions = useRef(false);

  // Single-select gets a leading "All" entry whose value is "".
  const entries: readonly FilterOption[] = multi
    ? def.options
    : [{ value: "", label: def.allLabel ?? "All" }, ...def.options];
  const isSelected = (value: string) =>
    multi ? selected.includes(value) : value === "" ? selected.length === 0 : selected[0] === value;
  // A long list has a search box: fifty sites cannot be scanned by eye. The "All" entry stays.
  const searchable = def.options.length > SEARCH_OPTIONS_OVER;
  const needle = find.trim().toLowerCase();
  const shown = needle
    ? entries.filter((entry) => entry.value === "" || entry.label.toLowerCase().includes(needle))
    : entries;

  const openList = useCallback(() => {
    const first = entries.findIndex((entry) => isSelected(entry.value));
    setIndex(first < 0 ? 0 : first);
    setFind("");
    inOptions.current = false;
    setOpen(true);
    // `entries`/`isSelected` are rebuilt every render and read only at open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOpen, selected, def]);

  useEffect(() => {
    if (!open) return;
    // A long list opens with the search box focused; the arrows then move into the options.
    if (searchable && !inOptions.current) {
      findRef.current?.focus();
      return;
    }
    optionRefs.current[index]?.focus();
  }, [open, index, searchable]);

  const choose = (entry: FilterOption) => {
    if (multi) {
      onChange(
        selected.includes(entry.value)
          ? selected.filter((v) => v !== entry.value)
          : [...selected, entry.value],
      );
    } else {
      onChange(entry.value === "" ? [] : [entry.value]);
      close();
    }
  };

  const onListKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const last = shown.length - 1;
    if (event.target instanceof HTMLInputElement) {
      // The search box keeps its own keys, except that Down goes to the first option.
      if (event.key === "ArrowDown" && last >= 0) {
        event.preventDefault();
        inOptions.current = true;
        setIndex(0);
        optionRefs.current[0]?.focus();
        return;
      }
      if (event.key !== "Escape" && event.key !== "Tab") return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setIndex((i) => Math.min(last, i + 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setIndex((i) => Math.max(0, i - 1));
        break;
      case "Home":
        event.preventDefault();
        setIndex(0);
        break;
      case "End":
        event.preventDefault();
        setIndex(last);
        break;
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        close();
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
    }
  };

  const active = selected.length > 0;
  const single = !multi ? def.options.find((option) => option.value === selected[0]) : undefined;

  return (
    <div className={styles.chipWrap}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.chip} ${active ? styles.chipOn : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        data-testid={`filter-${def.id}`}
        data-active={active}
        onClick={() => (open ? close(false) : openList())}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            openList();
          }
        }}
      >
        <span>{single ? `${def.label}: ${single.label}` : def.label}</span>
        {multi && active ? (
          <span className={styles.badge}>
            {selected.length}
            <span className="visuallyHidden"> selected</span>
          </span>
        ) : null}
        <CaretDown size={12} weight="bold" aria-hidden="true" />
      </button>
      {open ? (
        <div ref={popupRef} className={searchable ? `${styles.list} ${styles.listWithSearch}` : styles.list} onKeyDown={searchable ? onListKey : undefined}>
          {searchable ? (
            <input
              ref={findRef}
              type="search"
              className={styles.listSearch}
              aria-label={`Find in ${def.label}`}
              placeholder={`Find ${def.label.toLowerCase()}…`}
              value={find}
              onFocus={() => {
                inOptions.current = false;
              }}
              onChange={(event) => {
                setFind(event.target.value);
                setIndex(0);
              }}
              data-testid={`filter-find-${def.id}`}
            />
          ) : null}
        <div
          id={listId}
          role="listbox"
          aria-label={def.label}
          aria-multiselectable={multi}
          className={searchable ? styles.listScroll : undefined}
          onKeyDown={searchable ? undefined : onListKey}
        >
          {searchable && shown.length === 0 ? <div className={styles.listEmpty}>No match</div> : null}
          {shown.map((entry, i) => (
            <div
              key={entry.value}
              role="option"
              ref={(node) => {
                optionRefs.current[i] = node;
              }}
              aria-selected={isSelected(entry.value)}
              tabIndex={i === index ? 0 : -1}
              className={styles.option}
              data-value={entry.value}
              onClick={() => choose(entry)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  choose(entry);
                }
              }}
            >
              <span className={styles.tick} aria-hidden="true">
                {isSelected(entry.value) ? <Check size={13} weight="bold" /> : null}
              </span>
              <span className={styles.optionLabel}>{entry.label}</span>
              {entry.count !== undefined ? (
                <span className={styles.optionCount}>{number.format(entry.count)}</span>
              ) : null}
            </div>
          ))}
        </div>
        </div>
      ) : null}
    </div>
  );
}

export interface FilterBarProps {
  filters: readonly FilterDef[];
  state: FilterState;
  onChange: (next: FilterState) => void;
  searchLabel?: string;
  searchPlaceholder?: string;
  /** Wait this long after the last keystroke before emitting. 0 emits on every keystroke. */
  debounceMs?: number;
  /** `SavedViews`, or any node, at the start of the row. */
  savedViews?: ReactNode;
  /** Buttons at the end of the row: Export. */
  actions?: ReactNode;
  /** The server's counts, printed as "Showing {shown} of {total} {noun}". */
  resultCount?: { shown: number; total: number; noun?: string };
  testId?: string;
  /** One row that does not wrap (the table page's filter line, ADR-0212). At phone width it wraps
   * anyway: a row that cannot wrap there would push its controls off the screen. */
  singleLine?: boolean;
  /** Draw single-choice filters as native selects with our chevron, not as chips that open a list.
   * Multi-choice filters stay chips: a native select cannot hold several. */
  nativeSelects?: boolean;
  /** Draw the filters as an "Add filter" button and one removable chip per active filter, "Clear all"
   * at two or more, and the count as "38 of 214" (`tablePower/FilterChips`). The page's own
   * single-select native selects stay the default; this is the opt-in. */
  chipLine?: boolean;
  /** A column menu's request to open one filter's list (chip line only). */
  filterRequest?: { id: string; n: number } | null;
}

export function FilterBar({
  filters,
  state,
  onChange,
  searchLabel = "Search",
  searchPlaceholder,
  debounceMs = 250,
  savedViews,
  actions,
  resultCount,
  testId = "filter-bar",
  singleLine = false,
  nativeSelects = false,
  chipLine = false,
  filterRequest,
}: FilterBarProps) {
  const [draft, setDraft] = useState(state.search);
  const [seen, setSeen] = useState(state.search);
  const [typing, setTyping] = useState(false);
  // An outside change (Clear all, a saved view) replaces what is typed, unless a keystroke is still
  // waiting out its debounce (its own emission is what changes `state.search` next).
  if (state.search !== seen) {
    setSeen(state.search);
    if (!typing) setDraft(state.search);
  }
  const latest = useRef({ state, onChange });
  useEffect(() => {
    latest.current = { state, onChange };
  });
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const emitSearch = (value: string) => {
    const { state: current, onChange: emit } = latest.current;
    setTyping(false);
    if (value !== current.search) emit({ ...current, search: value });
  };
  const onType = (value: string) => {
    setDraft(value);
    setTyping(true);
    window.clearTimeout(timer.current);
    if (debounceMs <= 0) emitSearch(value);
    else timer.current = window.setTimeout(() => emitSearch(value), debounceMs);
  };

  const setValues = (id: string, next: readonly string[]) => {
    const values = { ...state.values };
    if (next.length === 0) delete values[id];
    else values[id] = next;
    onChange({ ...state, values });
  };

  const anyActive = chipLine
    ? activeFilterCount({ ...state, search: draft }) >= 2
    : activeFilterCount(state) > 0 || draft.trim() !== "";

  return (
    <div
      className={`${styles.bar} ${singleLine ? styles.barOne : ""}`}
      role="search"
      aria-label="Filters"
      data-testid={testId}
    >
      <div className={`${styles.row} ${singleLine ? styles.rowOne : ""}`}>
        {savedViews ? <div className={styles.saved}>{savedViews}</div> : null}
        <label className={styles.search}>
          <MagnifyingGlass size={15} aria-hidden="true" className={styles.searchIcon} />
          <span className="visuallyHidden">{searchLabel}</span>
          <input
            type="search"
            value={draft}
            placeholder={searchPlaceholder ?? `${searchLabel}…`}
            onChange={(event) => onType(event.target.value)}
            data-testid="filter-search"
          />
        </label>
        <div className={styles.chips}>
          <FunnelSimple size={15} aria-hidden="true" className={styles.funnel} />
          {chipLine ? (
            <FilterChips
              defs={filters}
              state={state}
              onChange={(next) => onChange({ ...next, search: state.search })}
              request={filterRequest}
            />
          ) : null}
          {(chipLine ? [] : filters).map((def) =>
            nativeSelects && def.mode === "single" ? (
              <NativeSelect
                key={def.id}
                def={def}
                selected={state.values[def.id] ?? []}
                onChange={(next) => setValues(def.id, next)}
              />
            ) : (
              <Chip
                key={def.id}
                def={def}
                selected={state.values[def.id] ?? []}
                onChange={(next) => setValues(def.id, next)}
              />
            ),
          )}
          {anyActive ? (
            <button
              type="button"
              className={styles.clear}
              data-testid="filter-clear"
              onClick={() => {
                window.clearTimeout(timer.current);
                setTyping(false);
                setDraft("");
                onChange(EMPTY_FILTERS);
              }}
            >
              <X size={12} weight="bold" aria-hidden="true" />
              {singleLine && !chipLine ? "Reset" : "Clear all"}
            </button>
          ) : null}
        </div>
        <div className={styles.end}>
          {resultCount ? (
            <p className={styles.count} role="status" data-testid="filter-count">
              {chipLine ? "" : "Showing "}
              {number.format(resultCount.shown)} of {number.format(resultCount.total)}
              {resultCount.noun ? ` ${resultCount.noun}` : ""}
            </p>
          ) : null}
          {actions}
        </div>
      </div>
    </div>
  );
}

/** Saved views as a row of toggle buttons. The views and their states are the caller's: this only
 * shows which one is active and reports a choice. */
export function SavedViews({
  views,
  activeId,
  onSelect,
  onSave,
  label = "Saved views",
}: {
  views: readonly { id: string; label: string }[];
  activeId?: string | null;
  onSelect: (id: string) => void;
  /** Present only when the caller can save a view; omitted means no control (not a disabled one). */
  onSave?: () => void;
  label?: string;
}) {
  return (
    <div className={styles.views} role="group" aria-label={label}>
      <BookmarkSimple size={15} aria-hidden="true" className={styles.funnel} />
      {views.map((view) => (
        <button
          key={view.id}
          type="button"
          className={`${styles.view} ${view.id === activeId ? styles.viewOn : ""}`}
          aria-pressed={view.id === activeId}
          onClick={() => onSelect(view.id)}
        >
          {view.label}
        </button>
      ))}
      {onSave ? (
        <button type="button" className={styles.save} onClick={onSave}>
          Save view
        </button>
      ) : null}
    </div>
  );
}
